import { ref, readonly, onBeforeUnmount } from "vue";
import type { Ref } from "vue";
import { useSipBroadcast } from "@/components/SIPIntegration/composables/useSipBroadcast";

// ─── Pure decision function ───────────────────────────────────────────────────

export type NotifChannel = "system" | "in-app" | "audio" | "title-flash";

/**
 * Determina quali canali di notifica attivare per una chiamata in arrivo.
 * Funzione pura: nessun side-effect, sempre testabile senza stub di browser API.
 */
export function decideNotificationChannels(o: {
  permission: NotificationPermission;
  documentHidden: boolean;
}): NotifChannel[] {
  const ch: NotifChannel[] = ["in-app", "audio", "title-flash"];
  if (o.permission === "granted") ch.unshift("system");
  return ch;
}

/**
 * Il banner "Chiamata in arrivo" va ritirato quando lo squillo è finito — che sia
 * per risposta, per rinuncia del chiamante o per timeout di coda.
 *
 * Prima di GAIA-2664 non veniva ritirato mai: restava appeso con un "Rispondi"
 * garantito inerte, perché la chiamata a cui si riferiva non esisteva più. È
 * l'altra faccia del tasto verde che sembra rotto.
 *
 * Il confronto sul numero non è cosmetico: `incoming-call` viaggia sul canale
 * post-only a livello di modulo, gli snapshot sull'istanza del componente. Sono due
 * oggetti `BroadcastChannel` distinti, quindi fra loro non c'è ordinamento
 * garantito e uno snapshot residuo della chiamata precedente non deve spegnere il
 * banner di quella nuova.
 */
export function shouldClearIncomingBanner(o: {
  announcedPhoneNumber: string | null;
  snapshotPhoneNumber: string;
  snapshotHasIncomingRing: boolean;
}): boolean {
  if (!o.announcedPhoneNumber) return false;
  if (o.snapshotHasIncomingRing) return false;
  return o.snapshotPhoneNumber === o.announcedPhoneNumber;
}

/** Messaggi mostrati sul banner quando la risposta non è andata a buon fine. */
export const ANSWER_FAILURE_MESSAGES = {
  "no-call": "La chiamata non è più disponibile: non è stato possibile risponderle.",
  "accept-failed":
    "Impossibile rispondere: il microfono non è disponibile. Controlla i permessi audio del browser.",
} as const;

/** Quanto resta visibile l'errore prima che il banner si ritiri da sé. */
const ANSWER_ERROR_VISIBLE_MS = 8_000;

// ─── Injectable effects interface ─────────────────────────────────────────────

export interface IncomingNotificationEffects {
  /** La linea arriva anche qui: con la tab nascosta il rettangolo di sistema è
   *  l'unica cosa che l'operatore vede prima di rispondere. */
  system: (phoneNumber: string, callerLine?: string | null) => void;
  audio: () => void;
  titleFlash: (phoneNumber: string) => void;
  inApp: (phoneNumber: string) => void;
}

// ─── Default real browser effects ─────────────────────────────────────────────

function defaultSystemEffect(phoneNumber: string, callerLine?: string | null): void {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

  new Notification("Chiamata in arrivo", {
    body: callerLine ? `${callerLine} · ${phoneNumber}` : phoneNumber,
    tag: "sip-incoming",
  });
}

function defaultAudioEffect(): void {
  if (typeof AudioContext === "undefined") return;
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => {
      ctx.close().catch(() => undefined);
    };
  } catch {
    // Non-critical: silently skip if AudioContext is unavailable or blocked.
  }
}

let titleFlashTimer: number | null = null;

function defaultTitleFlashEffect(phoneNumber: string): void {
  if (typeof document === "undefined") return;
  if (titleFlashTimer !== null) return; // already flashing for a concurrent call

  const original = document.title;
  const callLabel = `📞 ${phoneNumber}`;
  let toggled = false;
  let count = 0;
  const MAX_FLASHES = 10;

  titleFlashTimer = window.setInterval(() => {
    document.title = toggled ? original : callLabel;
    toggled = !toggled;
    count++;
    if (count >= MAX_FLASHES * 2) {
      clearInterval(titleFlashTimer!);
      titleFlashTimer = null;
      document.title = original;
    }
  }, 600);
}

function stopTitleFlash(): void {
  if (titleFlashTimer !== null) {
    clearInterval(titleFlashTimer);
    titleFlashTimer = null;
  }
}

// ─── Composable ───────────────────────────────────────────────────────────────

/** No-op default for `inApp` effect — signature satisfies `(phoneNumber: string) => void` via TS parameter covariance. */
const noopInApp: (phoneNumber: string) => void = () => undefined;

export function useSipIncomingNotification(effects?: Partial<IncomingNotificationEffects>): {
  requestPermission: () => void;
  incomingPhoneNumber: Readonly<Ref<string | null>>;
  incomingLeadUrl: Readonly<Ref<string | null>>;
  incomingCallerLine: Readonly<Ref<string | null>>;
  incomingError: Readonly<Ref<string | null>>;
} {
  const resolvedEffects: IncomingNotificationEffects = {
    system: effects?.system ?? defaultSystemEffect,
    audio: effects?.audio ?? defaultAudioEffect,
    titleFlash: effects?.titleFlash ?? defaultTitleFlashEffect,
    inApp: effects?.inApp ?? noopInApp,
  };

  const incomingPhoneNumber = ref<string | null>(null);
  // URL della scheda del chiamante quando è un contatto già esistente in Gaia.
  // Risolto dalla finestra owner e recapitato qui via `incoming-lead`: è così che
  // l'operatore che lavora nelle view può aprire la scheda senza cercarla a mano
  // (GAIA-2719).
  const incomingLeadUrl = ref<string | null>(null);
  // Linea dichiarata dal centralino per la chiamata annunciata a questa view.
  // Arriva dentro `incoming-call`, non da una lookup: è già nell'INVITE.
  const incomingCallerLine = ref<string | null>(null);
  // Errore da mostrare sul banner quando il "Rispondi" non ha risposto.
  const incomingError = ref<string | null>(null);
  /**
   * Ultimo numero annunciato a questa view. Distinto da `incomingPhoneNumber`
   * perché quello viene azzerato a fine squillo, mentre l'esito della risposta può
   * arrivare dopo: senza questo riferimento l'errore non troverebbe più la
   * chiamata a cui appartiene e resterebbe invisibile.
   */
  let announcedPhoneNumber: string | null = null;
  let errorDismissTimer: number | null = null;

  function clearErrorDismissTimer(): void {
    if (errorDismissTimer !== null) {
      clearTimeout(errorDismissTimer);
      errorDismissTimer = null;
    }
  }

  const broadcast = useSipBroadcast();

  broadcast.onIncomingLead(({ phoneNumber, targetUrl }) => {
    // Invariante: l'URL esposto si riferisce SEMPRE alla chiamata annunciata a
    // QUESTA view. Serve alle tab aperte o ricaricate dopo l'annuncio, che
    // possono ricevere `incoming-lead` senza aver visto il suo `incoming-call`.
    if (phoneNumber !== incomingPhoneNumber.value) return;
    incomingLeadUrl.value = targetUrl;
  });

  broadcast.onIncomingCall(({ phoneNumber, callerLine }) => {
    // Chiamata nuova: la scheda e l'errore del chiamante precedente non valgono più.
    incomingLeadUrl.value = null;
    incomingError.value = null;
    // Riassegnata sempre, anche quando manca: una linea residua attribuirebbe
    // alla chiamata nuova il servizio di quella prima.
    incomingCallerLine.value = callerLine ?? null;
    clearErrorDismissTimer();
    announcedPhoneNumber = phoneNumber;

    const permission: NotificationPermission =
      typeof Notification !== "undefined" ? Notification.permission : "denied";
    const documentHidden = typeof document !== "undefined" ? document.hidden : false;

    const channels = decideNotificationChannels({ permission, documentHidden });

    for (const ch of channels) {
      switch (ch) {
        case "system":
          resolvedEffects.system(phoneNumber, callerLine ?? null);
          break;
        case "audio":
          resolvedEffects.audio();
          break;
        case "title-flash":
          resolvedEffects.titleFlash(phoneNumber);
          break;
        case "in-app":
          incomingPhoneNumber.value = phoneNumber;
          resolvedEffects.inApp(phoneNumber);
          break;
      }
    }
  });

  // Lo squillo è finito: l'owner lo dice a ogni cambio di stato via snapshot. Da
  // qui il banner si ritira, invece di restare appeso a una chiamata che non c'è
  // più. Un errore in corso ha la precedenza: è l'unica cosa che l'operatore deve
  // ancora leggere.
  broadcast.onCallStateSnapshot(({ payload }) => {
    if (incomingError.value) return;
    if (
      !shouldClearIncomingBanner({
        announcedPhoneNumber,
        snapshotPhoneNumber: payload.phoneNumber,
        snapshotHasIncomingRing: payload.hasIncomingRing,
      })
    ) {
      return;
    }
    incomingPhoneNumber.value = null;
    incomingLeadUrl.value = null;
    incomingCallerLine.value = null;
  });

  // Il "Rispondi" non ha risposto. Il banner torna a galla in stato di errore: il
  // click è partito da questa tab, ed è qui che l'operatore deve leggere il perché.
  broadcast.onAnswerFailed(({ phoneNumber, reason }) => {
    if (phoneNumber !== announcedPhoneNumber) return;
    incomingPhoneNumber.value = phoneNumber;
    incomingError.value = ANSWER_FAILURE_MESSAGES[reason];
    clearErrorDismissTimer();
    errorDismissTimer = window.setTimeout(() => {
      errorDismissTimer = null;
      incomingError.value = null;
      incomingPhoneNumber.value = null;
      incomingLeadUrl.value = null;
      incomingCallerLine.value = null;
    }, ANSWER_ERROR_VISIBLE_MS);
  });

  function requestPermission(): void {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => undefined);
    }
  }

  onBeforeUnmount(() => {
    clearErrorDismissTimer();
    stopTitleFlash();
  });

  return {
    requestPermission,
    incomingPhoneNumber: readonly(incomingPhoneNumber),
    incomingLeadUrl: readonly(incomingLeadUrl),
    incomingCallerLine: readonly(incomingCallerLine),
    incomingError: readonly(incomingError),
  };
}
